import React, { useEffect, useState } from "react";
import AppModal from "../AppModal";
import { FiCheckCircle, FiXCircle, FiClock, FiDollarSign, FiShield, FiSmartphone } from "react-icons/fi";

const AllUserListModal = ({
  show,
  setShow,
  editData,
  viewMode = false,
}) => {
  const initialState = {
    fullName: "",
    mobile: "",
    referralCode: "",
    status: "",
    isKycVerified: false,
    lastLoginIp: "",
    deviceId: "",
    createdAt: "",
    // Nested objects initialize
    wallet: {
      depositBalance: 0,
      winningBalance: 0,
      bonusBalance: 0,
      cashbackBalance: 0,
      lockedBalance: 0,
    },
    kyc: {
      verificationStatus: "PENDING",
      documentType: "",
      documentNumber: "",
      aadhaarFrontImage: "",
      aadhaarBackImage: "",
      panImage: "",
      fullName: "",
      email: "",
    }
  };

  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    if (editData) {
      setFormData({
        ...initialState,
        ...editData,
        // ✅ Overwrite nested objects if they exist in API
        wallet: editData.wallet || initialState.wallet,
        kyc: editData.kyc || initialState.kyc,
      });
    } else {
      setFormData(initialState);
    }
  }, [editData, show]);

  // ✅ Helper function to format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Helper to show KYC Status badge
  const getKycStatusBadge = (status) => {
    if (status === "VERIFIED") {
      return <span className="badge bg-success fs-6"><FiCheckCircle /> Verified</span>;
    } else if (status === "REJECTED") {
      return <span className="badge bg-danger fs-6"><FiXCircle /> Rejected</span>;
    } else {
      return <span className="badge bg-warning fs-6"><FiClock /> Pending</span>;
    }
  };

  // ✅ Image URL Fix (If only filename comes from backend)
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://via.placeholder.com/200?text=No+Image";
    if (imagePath.startsWith("http")) return imagePath;
    // Assuming backend runs on localhost:5000
    return `http://192.168.1.5:5000/uploads/kyc/${imagePath}`;
  };

  return (
    <AppModal
      show={show}
      setShow={setShow}
      size="modal-lg"
      title={viewMode ? "View User Details" : "Edit User"}
      footer={
        <>
          <button className="btn btn-danger" onClick={() => setShow(false)}>
            Close
          </button>
        </>
      }
    >
      <div className="row">
        {/* Profile Image Section */}
        <div className="col-md-12 mb-4 text-center">
          {formData.profileImage ? (
            <img
              src={formData.profileImage}
              width="80"
              height="80"
              className="rounded-circle border shadow-sm"
              alt="Profile"
            />
          ) : (
            <div
              className="rounded-circle bg-secondary text-white mx-auto d-flex align-items-center justify-content-center border shadow-sm"
              style={{
                width: "80px",
                height: "80px",
                fontSize: "32px",
                fontWeight: "bold",
              }}
            >
              {formData.fullName?.charAt(0)?.toUpperCase() ||
               formData.mobile?.charAt(0) ||
               "U"}
            </div>
          )}
          <div className="mt-2">
            <h5 className="mb-0">
              {formData.fullName && formData.fullName.trim() !== ""
                ? formData.fullName
                : `User (${formData.mobile})`}
            </h5>
            <small className="text-muted">ID: {formData._id || "N/A"}</small>
          </div>
        </div>

        {/* Full Name */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Full Name</label>
          <input
            className="form-control"
            value={formData.fullName && formData.fullName.trim() !== "" ? formData.fullName : "N/A"}
            disabled
          />
        </div>

        {/* Mobile */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Mobile Number</label>
          <input className="form-control" value={formData.mobile || "N/A"} disabled />
        </div>

        {/* Referral Code */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Referral Code</label>
          <input className="form-control" value={formData.referralCode || "N/A"} disabled />
        </div>

        {/* Status */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Account Status</label>
          <input
            className="form-control"
            value={
              formData.status === "ACTIVE"
                ? "Active"
                : formData.status === "INACTIVE"
                ? "Inactive"
                : formData.status || "N/A"
            }
            disabled
          />
        </div>

        {/* ====== WALLET SECTION ====== */}
        <div className="col-12 mt-3 mb-2">
          <h6 className="fw-bold border-bottom pb-2 text-primary d-flex align-items-center gap-2">
            <FiDollarSign /> Wallet Details
          </h6>
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-bold text-muted small">Deposit Balance</label>
          <input
            className="form-control fw-bold text-success"
            value={`₹ ${formData.wallet?.depositBalance?.toLocaleString() || 0}`}
            disabled
          />
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-bold text-muted small">Winning Balance</label>
          <input
            className="form-control fw-bold text-warning"
            value={`₹ ${formData.wallet?.winningBalance?.toLocaleString() || 0}`}
            disabled
          />
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-bold text-muted small">Bonus Balance</label>
          <input
            className="form-control fw-bold text-primary"
            value={`₹ ${formData.wallet?.bonusBalance?.toLocaleString() || 0}`}
            disabled
          />
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-bold text-muted small">Cashback Balance</label>
          <input
            className="form-control fw-bold text-info"
            value={`₹ ${formData.wallet?.cashbackBalance?.toLocaleString() || 0}`}
            disabled
          />
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-bold text-muted small">Locked Balance</label>
          <input
            className="form-control fw-bold text-secondary"
            value={`₹ ${formData.wallet?.lockedBalance?.toLocaleString() || 0}`}
            disabled
          />
        </div>

        {/* ====== KYC SECTION ====== */}
        <div className="col-12 mt-3 mb-2">
          <h6 className="fw-bold border-bottom pb-2 text-success d-flex align-items-center gap-2">
            <FiShield /> KYC Details
          </h6>
        </div>

        {/* KYC Status */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">KYC Status</label>
          <div>
            {getKycStatusBadge(formData.kyc?.verificationStatus)}
          </div>
        </div>

        {/* KYC Name & Email */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">KYC Name</label>
          <input
            className="form-control"
            value={formData.kyc?.fullName || "N/A"}
            disabled
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">KYC Email</label>
          <input
            className="form-control"
            value={formData.kyc?.email || "N/A"}
            disabled
          />
        </div>

        {/* Doc Type & Number */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Document Type</label>
          <input
            className="form-control"
            value={formData.kyc?.documentType || "N/A"}
            disabled
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Document Number</label>
          <input
            className="form-control"
            value={formData.kyc?.documentNumber || "N/A"}
            disabled
          />
        </div>

        {/* KYC Images Section */}
        <div className="col-12">
          <label className="form-label fw-bold text-muted small mb-3">KYC Uploaded Images</label>
          <div className="row g-3">
            {/* Aadhaar Front */}
            <div className="col-md-4 text-center">
              <div className="small fw-bold mb-2">Aadhaar Front</div>
              <img
                src={getImageUrl(formData.kyc?.aadhaarFrontImage)}
                className="img-fluid rounded border shadow-sm"
                style={{ height: "120px", objectFit: "cover", width: "100%" }}
                alt="Aadhaar Front"
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = "https://via.placeholder.com/200?text=No+Image";
                }}
              />
            </div>

            {/* Aadhaar Back */}
            <div className="col-md-4 text-center">
              <div className="small fw-bold mb-2">Aadhaar Back</div>
              <img
                src={getImageUrl(formData.kyc?.aadhaarBackImage)}
                className="img-fluid rounded border shadow-sm"
                style={{ height: "120px", objectFit: "cover", width: "100%" }}
                alt="Aadhaar Back"
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = "https://via.placeholder.com/200?text=No+Image";
                }}
              />
            </div>

            {/* PAN Card */}
            <div className="col-md-4 text-center">
              <div className="small fw-bold mb-2">PAN Card</div>
              <img
                src={getImageUrl(formData.kyc?.panImage)}
                className="img-fluid rounded border shadow-sm"
                style={{ height: "120px", objectFit: "cover", width: "100%" }}
                alt="PAN Card"
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = "https://via.placeholder.com/200?text=No+Image";
                }}
              />
            </div>
          </div>
        </div>

        {/* ====== DEVICE & LOGIN DETAILS ====== */}
        <div className="col-12 mt-4 mb-2">
          <h6 className="fw-bold border-bottom pb-2 text-secondary d-flex align-items-center gap-2">
            <FiSmartphone /> Device & Login Info
          </h6>
        </div>

        {/* Device ID */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Device ID</label>
          <input className="form-control" value={formData.deviceId || "N/A"} disabled />
        </div>

        {/* Last Login IP */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Last Login IP</label>
          <input className="form-control" value={formData.lastLoginIp || "N/A"} disabled />
        </div>

        {/* Created Date */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Account Created</label>
          <input className="form-control" value={formatDate(formData.createdAt)} disabled />
        </div>

        {/* Last Login Date */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-muted small">Last Login</label>
          <input className="form-control" value={formatDate(formData.lastLogin)} disabled />
        </div>
      </div>
    </AppModal>
  );
};

export default AllUserListModal;